import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { MILESTONE_TYPES, ACTION_OWNERS } from '@/lib/types/milestone';
import type { CreateMilestoneData, MilestoneType, MilestoneActionOwner } from '@/lib/types/milestone';

const milestoneSchema = z.object({
  milestone_name: z.string().min(1, 'Milestone name is required'),
  due_date: z.string().optional(),
  description: z.string().optional(),
  milestone_type: z.enum(['planning', 'design', 'development', 'review', 'testing', 'delivery', 'client_approval', 'installation']),
  deliverable: z.string().min(1, 'Deliverable is required'),
  action_owner: z.enum(['client', 'vendor']),
  milestone_order: z.number().min(1),
  is_blank: z.boolean().default(false),
  show_upload_button: z.boolean().default(false)
});

type MilestoneFormData = z.infer<typeof milestoneSchema>;

interface MilestoneFormProps {
  onSubmit: (data: MilestoneFormData) => void;
  loading?: boolean;
  initialData?: Partial<MilestoneFormData>;
  nextOrder?: number;
  mode?: 'create' | 'edit' | 'requestChange';
  changeReason?: string;
  onChangeReasonUpdate?: (reason: string) => void;
}

export function MilestoneForm({ 
  onSubmit, 
  loading = false, 
  initialData, 
  nextOrder = 1,
  mode = 'create',
  changeReason = '',
  onChangeReasonUpdate
}: MilestoneFormProps) {
  // Convert ISO date to datetime-local format
  const formatDateForInput = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const form = useForm<MilestoneFormData>({
    resolver: zodResolver(milestoneSchema),
    defaultValues: {
      milestone_name: initialData?.milestone_name || '',
      due_date: formatDateForInput(initialData?.due_date),
      description: initialData?.description || '',
      milestone_type: initialData?.milestone_type || 'planning',
      deliverable: initialData?.deliverable || '',
      action_owner: initialData?.action_owner || 'client',
      milestone_order: initialData?.milestone_order || nextOrder,
      is_blank: initialData?.is_blank || false,
      show_upload_button: initialData?.show_upload_button || false
    }
  });

  const handleSubmit = (data: MilestoneFormData) => {
    // Convert datetime-local to UTC ISO string to preserve timezone
    const processedData = {
      ...data,
      due_date: data.due_date ? new Date(data.due_date).toISOString() : undefined
    };
    onSubmit(processedData);
    form.reset();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="milestone_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Milestone Name *</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Enter milestone name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="due_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Due Date</FormLabel>
                <FormControl>
                  <Input {...field} type="datetime-local" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="milestone_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Milestone Type *</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select milestone type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {MILESTONE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="action_owner"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Action Owner *</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select action owner" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {ACTION_OWNERS.map((owner) => (
                      <SelectItem key={owner.value} value={owner.value}>
                        {owner.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="deliverable"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Deliverable *</FormLabel>
              <FormControl>
                <Input {...field} placeholder="What specific output is expected?" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea {...field} placeholder="Optional description" rows={3} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Order field - only shown in edit mode */}
        {mode === 'edit' && (
          <FormField
            control={form.control}
            name="milestone_order"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Order</FormLabel>
                <FormControl>
                  <Input {...field} type="number" min={1} onChange={(e) => field.onChange(parseInt(e.target.value))} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="show_upload_button"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>
                  Show Upload Button
                </FormLabel>
                <p className="text-sm text-muted-foreground">
                  When enabled, the action owner will see an 'Upload Files' button on this milestone card
                </p>
              </div>
            </FormItem>
          )}
        />

        {/* Change Reason (only for change requests) */}
        {mode === 'requestChange' && (
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Reason for Change
            </label>
            <Textarea
              placeholder="Please explain why this change is needed..."
              value={changeReason}
              onChange={(e) => onChangeReasonUpdate?.(e.target.value)}
            />
          </div>
        )}

        <Button type="submit" disabled={loading}>
          {loading ? 'Saving...' : mode === 'requestChange' ? 'Request Change' : initialData ? 'Update Milestone' : 'Create Milestone'}
        </Button>
      </form>
    </Form>
  );
}